import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

import { ReportTabsProps, SavedReport } from './ReportTabs.props';

const ALL_OPEN_JOBS_LABEL = 'All Open Jobs';

export const ReportTabs: React.FC<ReportTabsProps> = ({
  activeReport,
  reports,
  onReportChange,
  onSave,
}) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [pinnedCodes, setPinnedCodes] = useState<string[]>([]);
  const [hoveredCode, setHoveredCode] = useState<string | null>(null);

  const menuOpen = Boolean(menuAnchor);

  const handleMenuOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget), []);

  const handleMenuClose = useCallback(() => setMenuAnchor(null), []);

  const handlePinReport = useCallback((code: string) => {
    setPinnedCodes((prev) => (prev.includes(code) ? prev : [...prev, code]));
    setMenuAnchor(null);
    onReportChange(code);
  }, [onReportChange]);

  const handleUnpinReport = useCallback(
    (code: string) => {
      setPinnedCodes((prev) => prev.filter((c) => c !== code));
      if (activeReport === code) {
        onReportChange(null);
      }
    },
    [activeReport, onReportChange]
  );

  const handleTabClick = useCallback(
    (code: string | null) => onReportChange(code), [onReportChange]);

  const pinnedReports = useMemo(
    () => reports.filter((r) => pinnedCodes.includes(r.code)),
    [reports, pinnedCodes]
  );

  const myReports = useMemo(
    () => reports.filter((r) => r.type === 'my'),
    [reports]
  );

  const sharedReports = useMemo(
    () => reports.filter((r) => r.type === 'shared'),
    [reports]
  );

  const tabSx = (isActive: boolean) => ({
    px: 2,
    py: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    borderBottom: '2px solid',
    borderColor: isActive ? theme.palette.v4.accent : 'transparent',
    color: isActive ? theme.palette.v4.accent : 'text.secondary',
    fontWeight: isActive ? 600 : 400,
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    '&:hover': {
      color: theme.palette.v4.accent,
    },
  });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        borderBottom: `1px solid ${theme.palette.v4.border}`,
        bgcolor: theme.palette.v4.surface,
      }}
    >
      {/* Default tab */}
      <Box
        role="tab"
        aria-selected={activeReport === null}
        tabIndex={0}
        onClick={() => handleTabClick(null)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleTabClick(null);
        }}
        sx={tabSx(activeReport === null)}
      >
        <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
          {ALL_OPEN_JOBS_LABEL}
        </Typography>
      </Box>

      {/* Pinned report tabs */}
      {pinnedReports.map((report) => (
        <Box
          key={report.code}
          role="tab"
          aria-selected={activeReport === report.code}
          tabIndex={0}
          onClick={() => handleTabClick(report.code)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleTabClick(report.code);
          }}
          onMouseEnter={() => setHoveredCode(report.code)}
          onMouseLeave={() => setHoveredCode(null)}
          sx={tabSx(activeReport === report.code)}
        >
          <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
            {report.name}
          </Typography>
          {hoveredCode === report.code && (
            <IconButton
              size="small"
              aria-label={`Close ${report.name} tab`}
              onClick={(e) => {
                e.stopPropagation();
                handleUnpinReport(report.code);
              }}
              sx={{ p: 0.25, ml: 0.5 }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>
      ))}

      {/* Add report button */}
      <IconButton
        size="small"
        aria-label="Add report tab"
        onClick={handleMenuOpen}
        sx={{
          mx: 0.5,
          alignSelf: 'center',
          color: 'text.secondary',
          '&:hover': { color: theme.palette.v4.accent },
        }}
      >
        <AddIcon sx={{ fontSize: 18 }} />
      </IconButton>

      {/* Reports menu */}
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: { minWidth: 220, maxHeight: 400 },
          },
        }}
      >
        {myReports.length > 0 && (
          <ListSubheader sx={{ lineHeight: '32px', fontSize: '0.75rem' }}>
            My Reports
          </ListSubheader>
        )}
        {myReports.map((r) => (
          <ReportMenuItem
            key={r.code}
            report={r}
            onPin={handlePinReport}
          />
        ))}
        {sharedReports.length > 0 && (
          <ListSubheader sx={{ lineHeight: '32px', fontSize: '0.75rem' }}>
            Shared Reports
          </ListSubheader>
        )}
        {sharedReports.map((r) => (
          <ReportMenuItem
            key={r.code}
            report={r}
            onPin={handlePinReport}
          />
        ))}
      </Menu>

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Save button */}
      <Button
        size="small"
        startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
        onClick={onSave}
        aria-label="Save report"
        sx={{
          mr: 1,
          alignSelf: 'center',
          textTransform: 'none',
          color: 'text.secondary',
          fontSize: '0.8125rem',
          '&:hover': { color: theme.palette.v4.accent },
        }}
      >
        Save
      </Button>
    </Box>
  );
};

interface ReportMenuItemProps {
  report: SavedReport;
  onPin: (code: string) => void;
}

const ReportMenuItem: React.FC<ReportMenuItemProps> = ({ report, onPin }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <MenuItem
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onPin(report.code)}
      sx={{ display: 'flex', justifyContent: 'space-between' }}
    >
      <ListItemText primary={report.name} />
      {hovered && (
        <PushPinOutlinedIcon sx={{ fontSize: 16, ml: 1, color: 'text.secondary' }} />
      )}
    </MenuItem>
  );
};
